import { useState } from "react";
import { useBillingPeriod, emitBillingRefresh } from "@/hooks/useBillingPeriod";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/Button";
import { CalendarOff } from "lucide-react";
import { periodIdFromDate } from "@/lib/format";
import { CollectingView } from "./CollectingView";
import { InvoicingView } from "./InvoicingView";
import { NotifyingView } from "./NotifyingView";
import { ClosedView } from "./ClosedView";
import { InvoicingHeader } from "./InvoicingHeader";
import { ApiError, useApiMutation } from "@/lib/api";
import { useToast } from "@/hooks/useToast";

export default function InvoicingRoot() {
  const [periodId, setPeriodId] = useState(() => periodIdFromDate(new Date()));
  const [location, setLocation] = useState<string | null>(null);
  const { data: period, error, loading, refetch: _refetch } = useBillingPeriod(periodId);
  function refetch() { _refetch(); emitBillingRefresh(); }
  const { mutate, loading: starting } = useApiMutation();
  const { push } = useToast();

  async function startMonth() {
    try {
      await mutate("/api/billing/period", "POST", { period_id: periodId });
      push({ message: "Miesiąc rozpoczęty", tone: "success" });
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  const header = (
    <InvoicingHeader
      periodId={periodId}
      onPeriodChange={(id) => { setPeriodId(id); setLocation(null); }}
      stage={period?.stage ?? "collecting"}
      location={location}
      onLocationChange={setLocation}
    />
  );

  if (loading) return <><div className="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-border h-12" /><Skeleton className="h-64 w-full" /></>;
  if (error instanceof ApiError && error.status === 404) {
    return (
      <>
        {header}
        <EmptyState
          icon={<CalendarOff className="size-8" />}
          title="Brak okresu rozliczeniowego"
          description="Dla wybranego miesiąca nie ma jeszcze rekordu billing_period."
          action={<Button onClick={startMonth} loading={starting}>Rozpocznij miesiąc</Button>}
        />
      </>
    );
  }
  if (error || !period) {
    return <EmptyState title="Nie udało się pobrać okresu" description={error?.message ?? "Nieznany błąd"} />;
  }

  const Body =
    period.stage === "collecting" ? <CollectingView period={period} refetch={refetch} location={location} /> :
    period.stage === "invoicing"  ? <InvoicingView  period={period} refetch={refetch} location={location} /> :
    period.stage === "notifying"  ? <NotifyingView  period={period} refetch={refetch} location={location} /> :
    <ClosedView period={period} />;

  return (
    <>
      {header}
      {Body}
    </>
  );
}
